import { useContext, useState } from 'react'

import { useAiTagGenerationsApi, useRestoreTagGenerationMutation } from '@/common/api/modules/aiTag'
import { dateTimeStr } from '@/common/form/date'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Modal from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'

import { AITagGenerationModalContext } from './AITagGenerationModalProvider'

const AITagGenerationModalInner = ({
  siteId,
  onRestored
}: {
  siteId: number
  onRestored: () => void
}) => {
  const { hide } = useContext(AITagGenerationModalContext)
  const { data: generationsData, mutate } = useAiTagGenerationsApi({ site_id: siteId })
  const { trigger: restoreGeneration } = useRestoreTagGenerationMutation()
  const [restoringId, setRestoringId] = useState<number | null>(null)

  const generations = generationsData?.generations ?? []

  const { showModal: showConfirmDialog } = useContext(ConfirmDialogContext)
  const handleRestore = async (generationId: number) => {
    showConfirmDialog({
      message: 'このバージョンに保存されているタグで復元しますか？',
      noText: 'キャンセル',
      yesText: '復元する',
      onClickYes: async () => {
        setRestoringId(generationId)
        try {
          await restoreGeneration({ site_id: siteId, generation_id: generationId })
          mutate()
          onRestored()
          hide()
        } finally {
          setRestoringId(null)
        }
      }
    })
  }

  return (
    <ModalContent type={ModalContentType.FLEX_GROW}>
      {generations.length === 0 ? (
        <div className='py-4 text-center'>
          <Text size={12} color={TextColor.SECONDARY}>
            履歴がありません。AI解析を実行すると自動的に保存されます。
          </Text>
        </div>
      ) : (
        <Table noPagination>
          <thead>
            <TableRow head>
              <TableHeadItem type='thin'>
                <Text size={12}>バージョン名</Text>
              </TableHeadItem>
              <TableHeadItem type='thin'>
                <Text size={12}>保存日時</Text>
              </TableHeadItem>
              <TableHeadItem type='thin'>
                <Text size={12}>タグ数</Text>
              </TableHeadItem>
              <TableHeadItem />
            </TableRow>
          </thead>
          <tbody>
            {generations.map((gen) => (
              <TableRow key={gen.id}>
                <TableRowItem>
                  <Text>{gen.summary}</Text>
                </TableRowItem>
                <TableRowItem>
                  <Text>{gen.created_at ? dateTimeStr(gen.created_at) : '-'}</Text>
                </TableRowItem>
                <TableRowItem>
                  <Text>{gen.tag_count}</Text>
                </TableRowItem>
                <TableRowItem>
                  <Button
                    color={ButtonColor.PRIMARY}
                    onClick={() => handleRestore(gen.id)}
                    disabled={restoringId === gen.id}
                    loading={restoringId === gen.id}
                  >
                    {restoringId === gen.id ? '復元中...' : '復元'}
                  </Button>
                </TableRowItem>
              </TableRow>
            ))}
          </tbody>
        </Table>
      )}
    </ModalContent>
  )
}

const AITagGenerationModal = () => {
  const { modal, showing, hide } = useContext(AITagGenerationModalContext)

  return (
    <Modal show={showing}>
      <ModalHeader text='解析履歴' onClickClose={hide} />
      {modal.showing && (
        <AITagGenerationModalInner siteId={modal.siteId} onRestored={modal.onRestored} />
      )}
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} size='large' onClick={hide}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default AITagGenerationModal
